import { ClockIcon } from "@heroicons/react/24/outline";
import type { PlaylistRowHonesty } from "../../repositories/playlists";
import { DEFAULT_STALE_AFTER_DAYS, staleHint } from "./stalePlacements";

// The date and position cells shared by the three playlist tables.
//
// Stored playlist data mixes two kinds of date: ones SoundCharts reported, and ones
// Cadence recorded because it saw a placement present or absent. They must never look the
// same. "Entry date: 22 Sep" tells a label their song joined that playlist on the 22nd;
// if 22 Sep is only the day Cadence started watching a catalog record, that is a wrong
// number about their own release. So Cadence's own dates carry a marker, and a placement
// SoundCharts has stopped confirming says so instead of showing an old position as current.
//
// Live (SoundCharts-direct) rows have none of these flags, and render exactly as before.

type Row = PlaylistRowHonesty & {
  entryDate?: string | null;
  exitDate?: string | null;
  position?: number | null;
};

type DateFormatter = (value: string) => string;

interface CellProps {
  item: Row;
  /** Each table already has its own date format; the cells use it rather than a new one. */
  formatDate: DateFormatter;
}

const OBSERVED_ENTRY_HINT =
  "Cadence first saw this placement on this date. It may have been on the playlist before then.";
const OBSERVED_EXIT_HINT =
  "The last day Cadence saw this placement on the playlist. It left some time after this.";

/** Entry date, or "Since at least …" when the date is Cadence's first sighting. */
export const EntryDateCell = ({ item, formatDate }: CellProps) => {
  if (!item.entryDate) return <>—</>;
  const date = formatDate(item.entryDate);

  if (item.entryDateIsObserved) {
    return (
      <span title={OBSERVED_ENTRY_HINT}>
        Since at least {date}
      </span>
    );
  }
  return <>{date}</>;
};

/** Exit date, or "Last seen …" when the date is Cadence's last sighting. */
export const ExitDateCell = ({ item, formatDate }: CellProps) => {
  if (!item.exitDate) return <>—</>;
  const date = formatDate(item.exitDate);

  if (item.exitIsObserved) {
    return (
      <span title={OBSERVED_EXIT_HINT}>
        Last seen {date}
      </span>
    );
  }
  return <>{date}</>;
};

interface CurrentPositionCellProps extends CellProps {
  /** The stale threshold in force, for the hint. */
  staleAfterDays?: number;
}

/**
 * Current position, or "#12 · last confirmed …" for a stale placement: the last position
 * SoundCharts reported, then when, in grey with a clock. A stale position is however old
 * its confirmation is, so it never shows bare, as if it were today's.
 */
export const CurrentPositionCell = ({
  item,
  formatDate,
  staleAfterDays = DEFAULT_STALE_AFTER_DAYS,
}: CurrentPositionCellProps) => {
  if (item.isStale) {
    const hint = staleHint(staleAfterDays);
    return (
      // Plain inline text, so a narrow column wraps it like a sentence; each piece stays
      // whole and the clock stays beside the date.
      <span className="text-neutral-400" title={hint}>
        {item.position != null && <span className="whitespace-nowrap">#{item.position} · </span>}
        <span className="whitespace-nowrap">
          {item.lastConfirmedAt
            ? `last confirmed ${formatDate(item.lastConfirmedAt)}`
            : "not confirmed recently"}
          <ClockIcon className="inline-block w-3.5 h-3.5 ml-1 -mt-0.5" aria-label={hint} />
        </span>
      </span>
    );
  }
  return <>{item.position ?? "—"}</>;
};
